/**
 * useErpSync — ERP sync completion and timing context handlers.
 * Phase 3B: uses queryClient.setQueryData for cache updates
 * instead of direct state mutation + reloadState.
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SCENARIO_TREE_KEY, type EditorState, createInitialState } from '../../../shared/hooks/queries/useScenarioTree';

interface UseErpSyncParams {
    scenarioId: number;
}

export function useErpSync({ scenarioId }: UseErpSyncParams) {
    const queryClient = useQueryClient();

    const handleErpSyncComplete = useCallback((fieldId: number, newValue: any, fieldKey?: string | null) => {
        queryClient.setQueryData<EditorState>(
            [SCENARIO_TREE_KEY, scenarioId],
            (old) => {
                const prev = old ?? createInitialState();
                const newItems = { ...prev.Input.tree.items };

                for (const itemKey of Object.keys(newItems)) {
                    const item = newItems[itemKey];
                    if (!item.fields) continue;
                    for (const fieldName of Object.keys(item.fields)) {
                        const field = item.fields[fieldName];
                        if (field.id === fieldId) {
                            const updatedField = { ...field };
                            if (fieldKey && updatedField.currentValue?.value?.[fieldKey]) {
                                updatedField.currentValue = {
                                    ...updatedField.currentValue,
                                    value: {
                                        ...updatedField.currentValue.value,
                                        [fieldKey]: {
                                            ...updatedField.currentValue.value[fieldKey],
                                            value: newValue,
                                        },
                                    },
                                };
                            } else if (updatedField.currentValue) {
                                updatedField.currentValue = {
                                    ...updatedField.currentValue,
                                    value: newValue,
                                };
                            }
                            newItems[itemKey] = {
                                ...item,
                                fields: { ...item.fields, [fieldName]: updatedField },
                            };
                            return {
                                ...prev,
                                Input: {
                                    ...prev.Input,
                                    tree: { ...prev.Input.tree, items: newItems },
                                },
                            };
                        }
                    }
                }
                return prev;
            }
        );
    }, [queryClient, scenarioId]);

    const getTimingContext = useCallback((fieldId: number) => {
        const currentState = queryClient.getQueryData<EditorState>([SCENARIO_TREE_KEY, scenarioId]);
        if (!currentState) return null;

        const items = currentState.Input.tree.items;
        for (const itemKey of Object.keys(items)) {
            const item = items[itemKey];
            if (!item.fields) continue;
            for (const fieldName of Object.keys(item.fields)) {
                const field = item.fields[fieldName];
                if (field.id === fieldId && field.currentValue?.value) {
                    const tv = field.currentValue.value;
                    return {
                        start_date: tv.start_date?.value?.format?.('YYYY-MM-DD') || tv.start_date?.value || null,
                        end_date: tv.end_date?.value?.format?.('YYYY-MM-DD') || tv.end_date?.value || null,
                        periodicity: tv.periodicity?.value || 'monthly',
                    };
                }
            }
        }
        return null;
    }, [queryClient, scenarioId]);

    return { handleErpSyncComplete, getTimingContext };
}
